import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { DashboardEditorIndexedDBT } from "../../db";
import { defineDashboardEditorIndexedDB } from "../../db";
import { useEditorStore } from "../store/editor";
import type { DashboardBlockModelT, DashboardModelT } from "../../schema/dashboard";
import { DashboardBlockModel, DashboardModel } from "../../schema/dashboard";
import * as io from "io-ts";
import * as Either from "fp-ts/lib/Either";
import { pipe } from "fp-ts/lib/function";
import { PathReporter } from "io-ts/lib/PathReporter";
// import { useAvailableResources } from "../useAvailableResources";
import t from "../../../../../i18n";
import { DateTime } from "luxon";
import type { ZoomT } from "../../schema/block/plot";
import { useDashboardUnsaved } from "../useDashboardUnsaved";

export const DashboardEditorContext = createContext<ReturnType<
  typeof useCreateDashboardEditorContext
> | null>(null);

// const UpdatableValue = io.union([DashboardModel, DashboardBlockModel]);
// const UpdatableValues = io.array(UpdatableValue);
// type UpdatableValueT = io.TypeOf<typeof UpdatableValue>;

export const useCreateDashboardEditorContext = ({
  dashboardId,
  projectId: _projectId,
}: {
  dashboardId: string;
  projectId: string;
}) => {
  const [database, setDatabase] = useState<DashboardEditorIndexedDBT | null>(null);
  const [focusedBlock, setFocusedBlock] = useState<DashboardBlockModelT | null>(null);
  const [initialized, setInitialized] = useState(false);
  const store = useEditorStore(database?.document, dashboardId);
  const [errors, setErrors] = useState<io.Errors | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  //   const availableResources = useAvailableResources({ projectId });
  const [_, setUnsaved] = useDashboardUnsaved({ scope: dashboardId });
  /**
   * @description Setup the dashboard editor populating the editor IndexedDB from dashboards IndexedDB
   */
  const setup = useCallback(
    ({ dashboard, blocks }: { dashboard: DashboardModelT; blocks: DashboardBlockModelT[] }) =>
      store.putMany?.([dashboard, ...blocks]),
    [store],
  );

  const clear = useCallback(() => store?.clear?.(), [store]);

  const updateDashboard = useCallback(
    (value: DashboardModelT): Promise<void> | void =>
      pipe(
        value,
        DashboardModel.decode,
        Either.match(
          err => {
            setErrorMessage(PathReporter.report(Either.left(err))[0] || "Unknown error");
          },
          v => {
            setErrorMessage(null);
            setUnsaved(true);
            return store?.put?.(v) ?? Promise.resolve();
          },
        ),
      ),
    [setUnsaved, store],
  );

  const updateBlock = useCallback(
    <T extends DashboardBlockModelT>(value: T) =>
      pipe(
        value,
        DashboardBlockModel.decode,
        Either.match(
          errors => {
            const message = errors.find(e => typeof e.message === "string")?.message;
            const reporter = PathReporter.report(Either.left(errors))[0];
            setErrors(errors);
            setErrorMessage(message || reporter || "Unknown error");
          },
          v => {
            if (v.kind === "PLOT") {
              const zooms = v.config?.layers
                .flatMap(l => [l.zoom?.x, l.zoom?.y])
                .filter((z): z is NonNullable<ZoomT> => Boolean(z))
                .filter(({ min, max }) => {
                  const vmin =
                    (typeof min === "string" ? DateTime.fromISO(min).toMillis() : min) ?? -Infinity;
                  const vmax =
                    (typeof max === "string" ? DateTime.fromISO(max).toMillis() : max) ?? Infinity;
                  return vmin > vmax;
                });

              if (zooms.length > 0 && zooms[0]) {
                return setErrorMessage(t("schema.layer.zoom.gt", zooms[0]?.min, zooms[0]?.max));
              }
            }

            setErrors(null);
            setErrorMessage(null);
            setUnsaved(true);

            return (store?.put?.({ ...v, timestamp: Date.now() }) ?? Promise.resolve()).then(
              () =>
                // Perform update for JSON editor, in case of editor view switch
                Either.isRight(DashboardBlockModel.decode(v)) &&
                setFocusedBlock(v as DashboardBlockModelT),
            );
          },
        ),
      ),
    [setUnsaved, store],
  );

  const updateManyBlocks = useCallback(
    (values: DashboardBlockModelT[]): Promise<void> | void => {
      pipe(
        values,
        io.array(DashboardBlockModel).decode,
        Either.match(
          err => {
            const report = PathReporter.report(Either.left(err))[0];
            setErrorMessage(report || "Unknown error");
          },
          v => {
            void store?.putMany?.(v);
            setErrorMessage(null);
            setUnsaved(true);
          },
        ),
      );
    },
    [setUnsaved, store],
  );

  const deleteBlock = useCallback(
    (value: DashboardBlockModelT) => {
      pipe(
        value,
        DashboardBlockModel.decode,
        Either.match(
          err => {
            const report = PathReporter.report(Either.left(err))[0];
            setErrorMessage(report || "Unknown error");
          },
          v => {
            void store?.delete?.(v.id);
            setErrorMessage(null);
            setUnsaved(true);
          },
        ),
      );
    },
    [setUnsaved, store],
  );

  const createBlock = useCallback(
    (value: DashboardBlockModelT) => {
      pipe(
        value,
        DashboardBlockModel.decode,
        Either.match(
          err => {
            const report = PathReporter.report(Either.left(err))[0];
            setErrorMessage(report || "Unknown error");
          },
          v => {
            void store?.add?.(v);
            setErrorMessage(null);
            setUnsaved(true);
          },
        ),
      );
    },
    [setUnsaved, store],
  );

  const focus = useCallback(
    (block: DashboardBlockModelT | null) => {
      if (block?.id !== focusedBlock?.id) {
        setFocusedBlock(block);
        return true;
      }
      return false;
    },
    [focusedBlock?.id],
  );

  useEffect(() => {
    if (!database) void defineDashboardEditorIndexedDB().then(db => setDatabase(db));
  }, [database]);

  useEffect(() => {
    setInitialized(store.initialized);
  }, [store.initialized]);

  return useMemo(
    () => ({
      dashboardId,
      store,
      error: errorMessage,
      errors,
      block: focusedBlock,
      setup,
      clear,
      //   update,
      updateDashboard,
      updateBlock,
      updateManyBlocks,
      deleteBlock,
      createBlock,
      focus,
      initialized,
    }),
    [
      clear,
      dashboardId,
      errorMessage,
      errors,
      focus,
      focusedBlock,
      initialized,
      setup,
      store,
      updateBlock,
      updateDashboard,
      updateManyBlocks,
      deleteBlock,
      createBlock,
    ],
  );
};

export const useDashboardEditorContext = () => useContext(DashboardEditorContext);
